import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { User } from '@auth0/auth0-angular';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabel } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { Textarea } from 'primeng/textarea';
import { filter, take } from 'rxjs';
import { SupportApiClient } from 'app/core/api-clients';
import { emailTldValidator, noWhitespaceValidator } from 'app/core/form-validators';
import { CreateSupportRequestModel } from 'app/core/models';
import { LoadHelper, RxHelper } from 'app/core/services';
import { AuthStore } from 'app/core/stores';
import { SupportFormControls } from './support-form-controls';
import { SupportModalData } from './support-modal-data';

const NAME_MAX_LENGTH = 100;
const EMAIL_MAX_LENGTH = 320;
const SUBJECT_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 5000;
const MAX_ATTACHMENT_COUNT = 3;
const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS: readonly string[] = ['pdf', 'png', 'jpg', 'jpeg'];
const ALLOWED_ATTACHMENT_MIME_TYPES: readonly string[] = ['application/pdf', 'image/png', 'image/jpeg'];

@Component({
  selector: 'wl-support-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
    FloatLabel,
    Textarea,
  ],
  templateUrl: './support-modal.component.html',
  styleUrl: './support-modal.component.scss',
  providers: [LoadHelper, RxHelper],
})
export class SupportModalComponent implements OnInit {
  private readonly _ref = inject(DynamicDialogRef);

  private readonly _config = inject<DynamicDialogConfig<SupportModalData>>(DynamicDialogConfig);

  private readonly _formBuilder = inject(FormBuilder);

  private readonly _authStore = inject(AuthStore);

  private readonly _supportApiClient = inject(SupportApiClient);

  private readonly _loadHelper = inject(LoadHelper);

  private readonly _rxHelper = inject(RxHelper);

  private readonly _destroyRef = inject(DestroyRef);

  public readonly progressbar$ = this._loadHelper.progressbar$;

  readonly nameMaxLength: number = NAME_MAX_LENGTH;

  readonly emailMaxLength: number = EMAIL_MAX_LENGTH;

  readonly subjectMaxLength: number = SUBJECT_MAX_LENGTH;

  readonly descriptionMaxLength: number = DESCRIPTION_MAX_LENGTH;

  readonly maxAttachmentCount: number = MAX_ATTACHMENT_COUNT;

  readonly maxAttachmentSizeLabel: string = '5 MB';

  readonly maxTotalAttachmentSizeLabel: string = '10 MB';

  readonly acceptedAttachmentTypesLabel: string = 'PDF, PNG, JPG, JPEG';

  selectedAttachments: readonly File[] = [];

  hasAttachmentTypeError = false;

  hasAttachmentFileSizeError = false;

  hasAttachmentCountError = false;

  hasAttachmentTotalSizeError = false;

  readonly form: FormGroup<SupportFormControls> = this._formBuilder.nonNullable.group({
    name: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(NAME_MAX_LENGTH)]],
    email: ['', [Validators.required, Validators.email, emailTldValidator(), Validators.maxLength(EMAIL_MAX_LENGTH)]],
    subject: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(SUBJECT_MAX_LENGTH)]],
    description: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(DESCRIPTION_MAX_LENGTH)]],
  });

  // Bound to the template, so these are plain properties rather than getters:
  // getters bound to templates re-run on every change-detection cycle.
  isSubmitDisabled = true;

  hasSubmitAttempted = false;

  ngOnInit(): void {
    this.prefillName();
    this.prefillEmail();

    this.updateSubmitDisabled();
    this.form.statusChanges.pipe(this._rxHelper.autoUnsubscribe).subscribe(() => {
      this.updateSubmitDisabled();
    });
  }

  onAttachmentsSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const files: readonly File[] = input.files === null ? [] : Array.from(input.files);
    input.value = '';

    this.clearAttachmentErrors();

    if (files.length === 0) {
      this.updateSubmitDisabled();
      return;
    }

    const attachments: readonly File[] = [...this.selectedAttachments, ...files];
    if (attachments.length > MAX_ATTACHMENT_COUNT) {
      this.hasAttachmentCountError = true;
      this.updateSubmitDisabled();
      return;
    }

    if (files.some((file: File): boolean => !this.isAllowedAttachment(file))) {
      this.hasAttachmentTypeError = true;
      this.updateSubmitDisabled();
      return;
    }

    if (files.some((file: File): boolean => file.size > MAX_ATTACHMENT_SIZE_BYTES)) {
      this.hasAttachmentFileSizeError = true;
      this.updateSubmitDisabled();
      return;
    }

    const totalSize: number = attachments.reduce((size: number, file: File): number => size + file.size, 0);
    if (totalSize > MAX_TOTAL_ATTACHMENT_SIZE_BYTES) {
      this.hasAttachmentTotalSizeError = true;
      this.updateSubmitDisabled();
      return;
    }

    this.selectedAttachments = attachments;
    this.updateSubmitDisabled();
  }

  removeAttachment(index: number): void {
    this.selectedAttachments = this.selectedAttachments.filter(
      (_file: File, fileIndex: number): boolean => fileIndex !== index
    );
    this.clearAttachmentErrors();
    this.updateSubmitDisabled();
  }

  submit(): void {
    this.hasSubmitAttempted = true;
    this.trimFormValues();
    this.form.markAllAsTouched();

    if (this.form.invalid || this.hasAttachmentErrors()) {
      return;
    }

    const formValue = this.form.getRawValue();
    const model: CreateSupportRequestModel = {
      name: formValue.name,
      email: formValue.email,
      subject: formValue.subject,
      description: formValue.description,
      attachments: this.selectedAttachments,
    };

    // caughtError=false keeps failures on the error channel. The default (true) rewrites errors
    // onto next, which would close the dialog as if the inquiry had been sent.
    this._loadHelper
      .loading(this._supportApiClient.create(model), false, false)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        // Success is 202 Accepted. Signal the opener, which renders the confirmation toast in its
        // own component-scoped message context (consistent with the app's other dialogs).
        next: () => {
          this._ref.close(true);
        },
        // Failures are surfaced by the global HTTP error toast (errorInterceptor); keep the
        // dialog open so the typed inquiry is not lost and can be retried.
        error: () => {
          /* handled by the global error toast */
        },
      });
  }

  cancel(): void {
    this._ref.close();
  }

  // Name is resolved from the backend (GET /users/name) before the dialog opens and passed in
  // as dialog data. It's the authoritative display name; the Auth0 token doesn't reliably carry it.
  private prefillName(): void {
    const name: string = this._config.data?.name?.trim() ?? '';
    if (name.length > 0 && this.form.controls.name.pristine) {
      this.form.controls.name.setValue(name);
    }
  }

  private prefillEmail(): void {
    this._authStore.user$
      .pipe(
        filter((user: User | null | undefined): user is User => user != null),
        take(1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((user: User) => {
        if (this.form.controls.email.pristine) {
          this.form.controls.email.setValue(user.email?.trim() ?? '');
        }
      });
  }

  private trimFormValues(): void {
    const value = this.form.getRawValue();
    this.form.setValue({
      name: value.name.trim(),
      email: value.email.trim(),
      subject: value.subject.trim(),
      description: value.description.trim(),
    });
  }

  private isAllowedAttachment(file: File): boolean {
    const extension: string = file.name.split('.').pop()?.toLowerCase() ?? '';
    const hasAllowedExtension: boolean = ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension);
    const hasAllowedMimeType: boolean = ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type.toLowerCase());

    return hasAllowedExtension && hasAllowedMimeType;
  }

  private clearAttachmentErrors(): void {
    this.hasAttachmentTypeError = false;
    this.hasAttachmentFileSizeError = false;
    this.hasAttachmentCountError = false;
    this.hasAttachmentTotalSizeError = false;
  }

  private updateSubmitDisabled(): void {
    this.isSubmitDisabled = this.form.invalid || this.hasAttachmentErrors();
  }

  private hasAttachmentErrors(): boolean {
    return (
      this.hasAttachmentTypeError ||
      this.hasAttachmentFileSizeError ||
      this.hasAttachmentCountError ||
      this.hasAttachmentTotalSizeError
    );
  }
}
